import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tile,
} from '@carbon/react';
import { EmptyState } from 'components/empty-state';
import { Default } from 'components/icons';
import { Link } from 'components/link';
import { PaginatedTable } from 'components/table/paginated-table';
import { Section } from 'components/section';
import { usePagination } from 'lib/hooks/usePagination';
import { formatNumber, parseNumericValue } from 'utils/number';
import {
  AfeTargetResponse,
  CitationResponse,
  DocumentResponse,
} from 'types/api-responses';
import { TargetCitationMatchSection } from './target-citation-match-section';

const AFE_DOCUMENT_TYPE = 'Document::Afe';

const isAfeDocument = (document: DocumentResponse) =>
  document.type === AFE_DOCUMENT_TYPE;

const COST_CATEGORY_COLUMNS = [
  'Drilling',
  'Completion',
  'Facilities',
  'Contingencies & Overhead',
  'Other',
] as const;

// Names that are sums/rollups, not real categories — must be skipped to avoid
// double-counting against the row's grand_total.
const isTotalLikeName = (name: string) =>
  /\b(total|subtotal|sum)\b/i.test(name);

const matchesCanonical = (name: string, canonical: string) =>
  name.toLowerCase() === canonical.toLowerCase() ||
  name.toLowerCase().includes(canonical.toLowerCase());

// One entry from the cost tree we'll bucket into a canonical column. When the
// LLM emits subcategoryTotals (e.g., Intangible Costs → [Drilling, Completions,
// Facilities]) we treat the subcategories as the leaves; when it doesn't, the
// category itself is the leaf.
type CostLeaf = {
  name: string;
  parentName: string;
  amount: number;
  standardizedCategory: string | null;
};

const collectCostLeaves = (target: AfeTargetResponse): CostLeaf[] => {
  const totals = target.data?.costSummary?.categoryTotals ?? [];
  const leaves: CostLeaf[] = [];

  for (const cat of totals) {
    const catName = cat?.categoryName?.trim();
    if (!catName || isTotalLikeName(catName)) continue;

    const subs = (cat.subcategoryTotals ?? []).filter(
      (s) => s?.subcategoryName && !isTotalLikeName(s.subcategoryName.trim())
    );

    if (subs.length > 0) {
      for (const sub of subs) {
        leaves.push({
          name: sub.subcategoryName!.trim(),
          parentName: catName,
          amount: parseNumericValue(sub.subcategoryTotal) ?? 0,
          standardizedCategory:
            sub.standardizedCostCategory ??
            cat.standardizedCostCategory ??
            null,
        });
      }
    } else {
      leaves.push({
        name: catName,
        parentName: catName,
        amount: parseNumericValue(cat.categoryTotal) ?? 0,
        standardizedCategory: cat.standardizedCostCategory ?? null,
      });
    }
  }

  return leaves;
};

const leafBelongsToCanonical = (leaf: CostLeaf, canonical: string): boolean => {
  // Use standardizedCostCategory directly when available (new schema)
  if (leaf.standardizedCategory) {
    return leaf.standardizedCategory === canonical;
  }
  // Fallback to fuzzy match for legacy data without standardized fields
  if (canonical === 'Other') {
    const otherCanonicals = COST_CATEGORY_COLUMNS.filter((c) => c !== 'Other');
    // Goes to Other only if neither the leaf name nor its parent matches any
    // other canonical column.
    return !otherCanonicals.some(
      (c) =>
        matchesCanonical(leaf.name, c) || matchesCanonical(leaf.parentName, c)
    );
  }
  return (
    matchesCanonical(leaf.name, canonical) ||
    matchesCanonical(leaf.parentName, canonical)
  );
};

const findCategoryTotal = (
  target: AfeTargetResponse,
  canonicalName: string
): number | null => {
  const totals = target.data?.costSummary?.categoryTotals ?? [];

  // New schema: categoryTotals already carry standardizedCostCategory; use
  // categoryTotal directly instead of recursing into subcategories.
  if (totals.some((cat) => cat.standardizedCostCategory)) {
    const sum = totals
      .filter((cat) => cat.standardizedCostCategory === canonicalName)
      .reduce(
        (acc, cat) => acc + (parseNumericValue(cat.categoryTotal) ?? 0),
        0
      );
    return sum > 0 ? sum : null;
  }

  // Legacy schema: fall back to leaf-based fuzzy matching.
  const sum = collectCostLeaves(target)
    .filter((leaf) => leafBelongsToCanonical(leaf, canonicalName))
    .reduce((acc, leaf) => acc + leaf.amount, 0);
  return sum > 0 ? sum : null;
};

const collectAfeNumbers = (citations: CitationResponse[] | undefined) =>
  (citations ?? [])
    .filter((c) => c.provisionField === 'afe_number')
    .map((c) => c.provisionValue);

const AfeWellInformationSection = ({
  document,
}: {
  document: DocumentResponse;
}) => {
  return (
    <TargetCitationMatchSection
      document={document}
      documentType={AFE_DOCUMENT_TYPE}
      header="Well Information"
      provisionField="afe_target"
      extractedColumnHeader="Well Details from Document"
      extractedColumnTooltip="Well identifiers extracted from the AFE document."
      matchColumnHeader="Suggested Well Match"
      testId="afe-well-information-table"
      renderTargetType={(target) => target.wellType}
      targetTypeLabel="Well Type"
    />
  );
};

const AfeCostInformationSection = ({
  document,
}: {
  document: DocumentResponse;
}) => {
  const targets = isAfeDocument(document) ? (document.afeTargets ?? []) : [];
  const pageSize = 10;
  const { page, slicedItems, onPaginate } = usePagination<AfeTargetResponse>(
    targets,
    pageSize
  );

  if (!isAfeDocument(document)) return null;
  if (targets.length === 0) return null;

  const afeNumbers = collectAfeNumbers(document.citations);
  const categoryColumns = COST_CATEGORY_COLUMNS;

  return (
    <Section header="Cost Information">
      <PaginatedTable
        pageSizes={[10, 20, 30]}
        pageSize={pageSize}
        page={page}
        totalItems={targets.length}
        onPaginate={onPaginate}
        isLoading={false}
        emptyStateComponent={
          <Tile>
            <EmptyState
              headerText="No cost information"
              helperText="No cost data was extracted for this AFE document."
              IconComponent={Default}
            />
          </Tile>
        }
        data-testid="afe-cost-information-table"
      >
        <TableHead>
          <TableRow>
            <TableHeader>Well Details</TableHeader>
            <TableHeader>AFE Number</TableHeader>
            {categoryColumns.map((category) => (
              <TableHeader key={category}>{category}</TableHeader>
            ))}
            <TableHeader>Total</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {slicedItems.map((target, index) => {
            const targetIndex = (page - 1) * pageSize + index;
            const afeNumber =
              target.data?.afeNumber ??
              afeNumbers[targetIndex] ??
              afeNumbers[0] ??
              '—';
            const wellLabel =
              target.well?.uwi ??
              target.data?.wellName ??
              `AFE Target ${target.id}`;
            const cellValues = categoryColumns.map((category) =>
              findCategoryTotal(target, category)
            );
            const rowTotal = cellValues.reduce<number>(
              (acc, value) => acc + (value ?? 0),
              0
            );

            return (
              <TableRow key={target.id}>
                <TableCell>
                  {target.well?.href ? (
                    <Link to={target.well.href}>{wellLabel}</Link>
                  ) : (
                    wellLabel
                  )}
                </TableCell>
                <TableCell>{afeNumber}</TableCell>
                {categoryColumns.map((category, columnIndex) => (
                  <TableCell key={category}>
                    {formatNumber(
                      cellValues[columnIndex],
                      2,
                      2,
                      'currency',
                      true,
                      '—'
                    )}
                  </TableCell>
                ))}
                <TableCell>
                  {formatNumber(
                    rowTotal > 0 ? rowTotal : null,
                    2,
                    2,
                    'currency',
                    true,
                    '—'
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </PaginatedTable>
    </Section>
  );
};

export { AfeWellInformationSection, AfeCostInformationSection };
